import { useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getRoleEmployees } from '../api';
import {
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
} from './PremiumDesign';
import {
  SearchInput,
  SideDrawer,
  SkeletonBox,
  SkeletonText,
} from '@/components';
import { formatName } from '@/common/utils';
import { groupPermissions, formatModuleName } from '../utils/roleUtils';

const FULL_TAB_LIST = [
  { id: 'employees', label: 'Employees' },
  { id: 'permissions', label: 'Permissions' },
];

export function RoleDetailsModal({
  isOpen,
  onClose,
  roleId,
  companyId,
  roleName,
}) {
  const [activeTab, setActiveTab] = useState('employees');
  const [permPage, setPermPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [permSearchQuery, setPermSearchQuery] = useState('');
  const [debouncedPermSearch, setDebouncedPermSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPermSearch(permSearchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [permSearchQuery]);

  // Reset states when modal is opened
  useEffect(() => {
    if (isOpen) {
      setPermPage(1);
      setSearchQuery('');
      setDebouncedSearch('');
      setPermSearchQuery('');
      setDebouncedPermSearch('');
      setActiveTab('employees');
    }
  }, [isOpen, roleId]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [
        'role-details',
        companyId,
        roleId,
        debouncedSearch,
        debouncedPermSearch,
        permPage,
      ],
      queryFn: async ({ pageParam = 1 }) => {
        if (!companyId || !roleId) return null;

        const fetchPage = (page) => {
          const params = {};
          if (debouncedSearch) params.search = debouncedSearch;
          if (debouncedPermSearch) params.perm_search = debouncedPermSearch;
          if (page > 1) params.page = page;
          if (permPage > 1) params.perm_page = permPage;
          return getRoleEmployees({ companyId, roleId, params });
        };

        if (pageParam === 1) {
          const res1 = await fetchPage(1);
          if (res1?.employees?.next) {
            const res2 = await fetchPage(2);
            const combinedEmployees = {
              ...res1.employees,
              results: [
                ...(res1.employees?.results || []),
                ...(res2?.employees?.results || []),
              ],
              next: res2?.employees?.next,
            };
            return { ...res1, employees: combinedEmployees };
          }
          return res1;
        }
        return fetchPage(pageParam);
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextUrl = lastPage?.employees?.next;
        if (!nextUrl) return undefined;
        return allPages.length + 2;
      },
      enabled: isOpen && !!companyId && !!roleId,
    });

  const employeesList =
    data?.pages?.flatMap((page) => page?.employees?.results || []) || [];
  const permissionsList =
    data?.pages?.[0]?.permissions?.results ||
    data?.pages?.[0]?.permissions ||
    [];
  const displayRoleName =
    roleName ||
    data?.pages?.[0]?.role_name ||
    data?.pages?.[0]?.name ||
    'Loading...';
  const displayDescription = data?.pages?.[0]?.description;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      width="lg"
      showClose={false}
      bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
      headerClassName="!px-6 !py-5"
      header={
        <div className="flex items-center justify-between w-full gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-surface-mint flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-primary">
                {displayRoleName[0]?.toUpperCase() || 'R'}
              </span>
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-text-primary truncate">
                {formatName(displayRoleName) || 'Role Details'}
              </h3>
              {displayDescription && (
                <p
                  className="text-sm text-text-muted break-words"
                  title={displayDescription}
                >
                  {displayDescription}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.75"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* ── Tab Bar ── */}
        <div className="flex items-center justify-between px-6 pt-3 pb-0 border-b border-border-default shrink-0 overflow-x-auto overflow-y-hidden no-scrollbar">
          <div className="flex items-center">
            {FULL_TAB_LIST.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-all border-b-2 -mb-px focus:outline-none ${
                  activeTab === tab.id
                    ? 'text-primary border-primary'
                    : 'text-text-muted border-transparent hover:text-text-primary hover:border-border-default'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab Content ── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 flex flex-col bg-white">
          {activeTab === 'employees' && (
            <div className="flex flex-col gap-4 animate-in fade-in duration-300">
              <div className="flex items-center justify-start">
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search employees..."
                  className="w-56"
                  inputClassName="!h-9 text-xs"
                />
              </div>

              <div className="bg-white border border-border-default rounded-2xl p-4 flex flex-col overflow-hidden max-h-[650px]">
                <div
                  className="overflow-y-auto custom-scrollbar flex-1"
                  onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target;
                    if (
                      scrollHeight - scrollTop - clientHeight < 50 &&
                      hasNextPage &&
                      !isFetchingNextPage
                    ) {
                      fetchNextPage();
                    }
                  }}
                >
                  <DashboardTable>
                    <DashboardTableHeader>
                      <th className="py-3 px-4 text-left">Name</th>
                      <th className="py-3 px-4 text-left">Email</th>
                      <th className="py-3 px-4 text-left">Phone</th>
                    </DashboardTableHeader>
                    <DashboardTableBody>
                      {isLoading ? (
                        [...Array(5)].map((_, i) => (
                          <DashboardTableRow key={`skeleton-${i}`}>
                            <DashboardTableCell>
                              <SkeletonText width="w-32" height="h-4" />
                            </DashboardTableCell>
                            <DashboardTableCell>
                              <SkeletonText width="w-40" height="h-4" />
                            </DashboardTableCell>
                            <DashboardTableCell>
                              <SkeletonText width="w-32" height="h-4" />
                            </DashboardTableCell>
                          </DashboardTableRow>
                        ))
                      ) : employeesList.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="py-8 text-center text-text-muted text-sm"
                          >
                            No employees found for this role.
                          </td>
                        </tr>
                      ) : (
                        employeesList.map((emp) => (
                          <DashboardTableRow key={emp.id}>
                            <DashboardTableCell>
                              {emp.name || '-'}
                            </DashboardTableCell>
                            <DashboardTableCell>
                              {emp.email || '-'}
                            </DashboardTableCell>
                            <DashboardTableCell>
                              {emp.phone || '-'}
                            </DashboardTableCell>
                          </DashboardTableRow>
                        ))
                      )}
                    </DashboardTableBody>
                  </DashboardTable>
                  {isFetchingNextPage && (
                    <div className="flex flex-col border-t border-border-default">
                      {[...Array(2)].map((_, i) => (
                        <div
                          key={`skeleton-more-${i}`}
                          className="flex items-center gap-4 h-[56px] px-4 border-b border-border-default last:border-b-0"
                        >
                          <SkeletonText width="w-32" height="h-4" />
                          <SkeletonText width="w-40" height="h-4" />
                          <SkeletonText width="w-32" height="h-4" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="flex flex-col gap-4 h-full animate-in fade-in duration-300">
              <div className="flex items-center justify-start shrink-0">
                <SearchInput
                  value={permSearchQuery}
                  onChange={(e) => setPermSearchQuery(e.target.value)}
                  placeholder="Search permissions..."
                  className="w-56"
                  inputClassName="!h-9 text-xs"
                />
              </div>

              <div className="bg-white border border-border-default rounded-2xl p-4 flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="overflow-auto custom-scrollbar flex-1">
                  <table className="w-full text-left border-collapse min-w-[500px]">
                    <thead className="sticky top-0 bg-layer-5-card z-10 border-b border-border-default shadow-sm">
                      <tr>
                        <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider w-[40%]">
                          Menu / Modules
                        </th>
                        <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                          Read
                        </th>
                        <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                          Add
                        </th>
                        <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                          Edit
                        </th>
                        <th className="py-3 px-4 text-xs font-bold text-text-secondary uppercase tracking-wider text-center w-[15%]">
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-default">
                      {(() => {
                        if (isLoading) {
                          return [...Array(5)].map((_, i) => (
                            <tr key={`skeleton-perm-${i}`}>
                              <td className="py-3 px-4">
                                <SkeletonText width="w-32" height="h-4" />
                              </td>
                              {['read', 'add', 'edit', 'delete'].map(
                                (actionKey) => (
                                  <td
                                    key={actionKey}
                                    className="py-3 px-4 text-center align-middle"
                                  >
                                    <SkeletonBox className="w-5 h-5 mx-auto" />
                                  </td>
                                ),
                              )}
                            </tr>
                          ));
                        }

                        const groupedPerms = groupPermissions(
                          permissionsList,
                          debouncedPermSearch,
                        );
                        if (groupedPerms.length === 0) {
                          return (
                            <tr>
                              <td
                                colSpan={5}
                                className="py-8 text-center text-text-muted text-sm"
                              >
                                No permissions found.
                              </td>
                            </tr>
                          );
                        }

                        return groupedPerms.map((group, idx) => {
                          const groupName = formatModuleName(group.name);
                          return (
                            <tr
                              key={idx}
                              className="hover:bg-layer-7-hover/30 transition-colors"
                            >
                              <td className="py-3 px-4">
                                <span className="text-sm font-semibold text-text-primary">
                                  {groupName}
                                </span>
                              </td>
                              {['read', 'add', 'edit', 'delete'].map(
                                (actionKey) => {
                                  const perm = group.actions[actionKey];
                                  return (
                                    <td
                                      key={actionKey}
                                      className="py-3 px-4 text-center align-middle"
                                    >
                                      {perm ? (
                                        <div
                                          className="flex justify-center"
                                          title={
                                            perm.name ||
                                            `${groupName} - ${actionKey}`
                                          }
                                        >
                                          <div className="relative flex items-center justify-center w-5 h-5 bg-primary border border-primary rounded">
                                            <svg
                                              className="w-3.5 h-3.5 text-white"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="3"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-text-muted/30">
                                          -
                                        </span>
                                      )}
                                    </td>
                                  );
                                },
                              )}
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SideDrawer>
  );
}
